// Los campos que se pueden insertar en los mensajes de los KITS (bienvenida y
// cambio de vida), en un solo sitio.
//
// Antes la misma lista estaba escrita a mano en TRES lugares y ninguno decia lo
// mismo:
//   • deskte-full.jsx  — editor del kit desde Renovaciones: fichas con
//     {cliente} {compania} {tipo} {poliza#}, SIN portal.
//   • OnboardingView.jsx — editor por tipo de poliza: texto plano
//     "Etiquetas: {cliente} · {compania} · …", sin nada donde hacer clic
//     ("asi con corchete se ve horrible", Lissette 2026-09-01).
//   • LifeChangeKitModal.jsx — el kit de cambio de vida: ningun campo.
// Tres copias de la misma idea derivan (E-138). Esta es la copia buena.
//
// SOBRE LOS ACENTOS: el servidor acepta las dos formas
// (`fillKitPlaceholders` en policies.routes.ts usa /\{compa[ñn][íi]a\}/gi y
// /\{p(?:ó|o)liza#?\}/gi), y las plantillas ya guardadas por las agencias
// llevan las acentuadas. Las fichas insertan la forma SIN acento —una etiqueta
// que se escribe igual en cualquier teclado— y `rellenarCamposDelKit` de aqui
// abajo entiende las dos, asi que nada de lo ya guardado se rompe.

// {portal} NO va en esta lista de relleno: lo resuelve el SERVIDOR al enviar
// (`ponerEnlaceDelPortal`, utils/portalLink.ts), porque hay que crear el enlace
// del portal —y el portal mismo si el cliente no lo tiene— y eso el navegador
// no lo puede hacer.
export const CAMPOS_DEL_KIT = [
  { tag: '{cliente}', es: 'Cliente', en: 'Client' },
  { tag: '{compania}', es: 'Compañía', en: 'Company' },
  { tag: '{tipo}', es: 'Tipo de póliza', en: 'Policy type' },
  { tag: '{poliza}', es: 'N.º de póliza', en: 'Policy #' },
  // Lissette 2026-10-16: *"faltan campos para la creación del kit de
  // bienvenida: Plan, Ingreso, Prima mensual, Fecha de efectividad"*. El
  // servidor los rellena igual (`valoresDelKit` en utils/cuerpoDelKit.ts).
  { tag: '{plan}', es: 'Plan', en: 'Plan' },
  { tag: '{ingreso}', es: 'Ingreso', en: 'Income' },
  { tag: '{prima}', es: 'Prima mensual', en: 'Monthly premium' },
  { tag: '{efectividad}', es: 'Fecha de efectividad', en: 'Effective date' },
  { tag: '{portal}', es: 'Enlace del portal', en: 'Portal link' },
];

// Fichas listas para <VarChips vars={...}>: { tag, label }.
export function chipsDelKit(lang = 'es') {
  const es = lang === 'es';
  return CAMPOS_DEL_KIT.map((c) => ({ tag: c.tag, label: es ? c.es : c.en }));
}

// Rellena lo que SI se puede rellenar en el navegador. Tolerante a acentos, y
// deja {portal} intacto a proposito para que lo ponga el servidor.
export function rellenarCamposDelKit(texto, vars = {}) {
  const v = (x) => (x == null ? '' : String(x));
  return String(texto || '')
    .replace(/\{cliente\}/gi, v(vars.cliente))
    .replace(/\{compa[ñn][íi]a\}/gi, v(vars.compania))
    .replace(/\{tipo\}/gi, v(vars.tipo))
    .replace(/\{p(?:ó|o)liza#?\}/gi, v(vars.poliza))
    .replace(/\{plan\}/gi, v(vars.plan))
    .replace(/\{ingreso\}/gi, v(vars.ingreso))
    .replace(/\{prima\}/gi, v(vars.prima))
    .replace(/\{efectividad\}/gi, v(vars.efectividad));
}

/** "1234.5" -> "$1,234.50". Lo que no es un número se deja como se escribió. */
export function dineroDelKit(x, decimales = 2) {
  const s = String(x == null ? '' : x).trim();
  if (!s) return '';
  const n = Number(s.replace(/[$,\s]/g, ''));
  if (!Number.isFinite(n)) return s;
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: decimales, maximumFractionDigits: decimales });
}

/** "2026-01-01" -> "01/01/2026". Se leen las partes tal cual: sin pasar por la zona horaria. */
export function fechaDelKit(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
}

/**
 * Los valores de la póliza y de la ficha para los campos nuevos, con la forma
 * de la pantalla (`api.js`: `plan`, `monthlyPremium`, `effectiveDate`;
 * cliente `annualIncome`). El ingreso es texto libre en la ficha: si es un
 * número se escribe como dinero sin centavos; si no, tal cual.
 */
export function valoresDePolizaParaElKit(poliza, cliente) {
  return {
    plan: (poliza && poliza.plan) || '',
    prima: dineroDelKit(poliza && poliza.monthlyPremium),
    efectividad: fechaDelKit(poliza && poliza.effectiveDate),
    ingreso: dineroDelKit(cliente && cliente.annualIncome, 0),
  };
}
